/**
 * Singleton AudioContext + master mix chain:
 *
 *   musicBus  -\
 *                > masterGain -> lowpass (~7kHz) -> destination
 *   sfxBus    -/
 *
 * musicBus itself is split so the ambient/tension layers can be ducked
 * under a stinger without touching SFX or the user's master volume.
 *
 * The context is created lazily and resumed only on the first user gesture
 * (autoplay policies), with a visibilitychange fallback for iOS Safari,
 * which likes to suspend contexts when the tab is backgrounded.
 */

const VOLUME_KEY = 'tokenomics.audio.volume';
const MUTED_KEY = 'tokenomics.audio.muted';
const DEFAULT_VOLUME = 0.25;

export interface AudioGraph {
  ctx: AudioContext;
  masterGain: GainNode;
  lowpass: BiquadFilterNode;
  musicBus: GainNode;
  sfxBus: GainNode;
}

let graph: AudioGraph | null = null;
let volume = readStoredVolume();
let muted = readStoredMuted();

function readStoredVolume(): number {
  try {
    const raw = localStorage.getItem(VOLUME_KEY);
    const v = raw !== null ? parseFloat(raw) : DEFAULT_VOLUME;
    return Number.isFinite(v) ? Math.min(1, Math.max(0, v)) : DEFAULT_VOLUME;
  } catch {
    return DEFAULT_VOLUME;
  }
}

function readStoredMuted(): boolean {
  try {
    return localStorage.getItem(MUTED_KEY) === '1';
  } catch {
    return false;
  }
}

function persistVolume(v: number): void {
  try {
    localStorage.setItem(VOLUME_KEY, String(v));
  } catch {
    /* storage unavailable (private mode etc.) — in-memory state still works */
  }
}

function persistMuted(m: boolean): void {
  try {
    localStorage.setItem(MUTED_KEY, m ? '1' : '0');
  } catch {
    /* ignore */
  }
}

function applyMasterGain(): void {
  if (!graph) return;
  const target = muted ? 0 : volume;
  const g = graph.masterGain.gain;
  const now = graph.ctx.currentTime;
  g.cancelScheduledValues(now);
  g.setTargetAtTime(target, now, 0.03);
}

/** Build the graph once. Safe to call repeatedly. */
export function ensureGraph(): AudioGraph {
  if (graph) return graph;
  const AC = window.AudioContext || (window as unknown as { webkitAudioContext: typeof AudioContext }).webkitAudioContext;
  const ctx = new AC();

  const masterGain = ctx.createGain();
  masterGain.gain.value = muted ? 0 : volume;

  const lowpass = ctx.createBiquadFilter();
  lowpass.type = 'lowpass';
  lowpass.frequency.value = 7200;
  lowpass.Q.value = 0.4;

  const musicBus = ctx.createGain();
  musicBus.gain.value = 1;
  const sfxBus = ctx.createGain();
  sfxBus.gain.value = 1;

  musicBus.connect(masterGain);
  sfxBus.connect(masterGain);
  masterGain.connect(lowpass);
  lowpass.connect(ctx.destination);

  graph = { ctx, masterGain, lowpass, musicBus, sfxBus };
  return graph;
}

/** Wires one-time capture-phase gesture listeners that create/resume the
 * context. Call once at startup; does nothing once the context is running. */
export function armGestureResume(): void {
  const tryResume = (): void => {
    const g = ensureGraph();
    if (g.ctx.state !== 'running') void g.ctx.resume();
  };
  window.addEventListener('pointerdown', tryResume, { once: true, capture: true });
  window.addEventListener('keydown', tryResume, { once: true, capture: true });
  // iOS Safari suspends the context when the tab is backgrounded; resume
  // whenever it becomes visible again (still requires having unlocked once).
  document.addEventListener('visibilitychange', () => {
    if (document.visibilityState === 'visible' && graph && graph.ctx.state !== 'running') {
      void graph.ctx.resume();
    }
  });
}

export function getVolume(): number {
  return volume;
}

export function isMuted(): boolean {
  return muted;
}

export function setVolume(v: number): void {
  volume = Math.min(1, Math.max(0, v));
  persistVolume(volume);
  applyMasterGain();
}

export function setMuted(m: boolean): void {
  muted = m;
  persistMuted(muted);
  applyMasterGain();
}

export function toggleMuted(): boolean {
  setMuted(!muted);
  return muted;
}

/** Cycle 25% -> 50% -> 75% -> 25% ..., unmuting if currently muted. */
export function cycleVolumePreset(): number {
  const presets = [0.25, 0.5, 0.75];
  const idx = presets.findIndex((p) => Math.abs(p - volume) < 0.05);
  const next = presets[(idx + 1) % presets.length] ?? presets[0];
  setVolume(next);
  if (muted) setMuted(false);
  return next;
}
